// frontend/admin-portal/src/components/suppliers/SupplierLinkProductModal.jsx
import React, { useEffect, useState } from "react";
import { Modal, Button, Input, Alert } from "@fiberhome/shared-ui";
import { productService } from "../../services/productService";
import { supplierService } from "../../services/supplierService";

export default function SupplierLinkProductModal({
  open,
  supplier,
  onClose,
  onLinked,
  alreadyLinkedIds = [],
}) {
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(null);

  const [form, setForm] = useState({
    supplierSku: "",
    supplierCost: "",
    leadTimeDays: "",
    minOrderQty: 1,
    isPreferred: false,
    notes: "",
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setPicked(null);
    setForm({
      supplierSku: "",
      supplierCost: "",
      leadTimeDays: "",
      minOrderQty: 1,
      isPreferred: false,
      notes: "",
    });
    setError("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    productService
      .list({ search, limit: 30 })
      .then(({ items }) => alive && setProducts(items))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [open, search]);

  const handlePick = (p) => {
    setPicked(p);
    setForm((f) => ({
      ...f,
      supplierSku: "",
      supplierCost: p.costPrice || "",
    }));
  };

  const handleSubmit = async () => {
    if (!picked) {
      setError("Pick a product");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await supplierService.linkProduct(supplier.id, {
        productId: picked.id,
        supplierSku: form.supplierSku || null,
        supplierCost:
          form.supplierCost === "" ? null : Number(form.supplierCost),
        leadTimeDays:
          form.leadTimeDays === "" ? null : Number(form.leadTimeDays),
        minOrderQty: Number(form.minOrderQty) || 1,
        isPreferred: !!form.isPreferred,
        notes: form.notes || null,
      });
      onLinked?.();
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Link failed");
    } finally {
      setSaving(false);
    }
  };

  const already = new Set(alreadyLinkedIds.map(Number));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Link a product to ${supplier?.name || ""}`}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={saving}
            disabled={!picked}
          >
            Link product
          </Button>
        </>
      }
    >
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {!picked ? (
        <>
          <Input
            label="Search products"
            placeholder="Search by name or SKU..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="fh-link-list">
            {loading && <div className="text-muted p-3">Loading…</div>}
            {!loading && products.length === 0 && (
              <div className="text-muted p-3">No products found</div>
            )}
            {!loading &&
              products.map((p) => {
                const isLinked = already.has(Number(p.id));
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={`fh-link-item ${isLinked ? "is-linked" : ""}`}
                    onClick={() => !isLinked && handlePick(p)}
                    disabled={isLinked}
                  >
                    <div>
                      <div className="fh-link-item__name">{p.name}</div>
                      <div className="fh-link-item__sub">
                        {p.sku} {p.category ? `· ${p.category.name}` : ""}
                      </div>
                    </div>
                    {isLinked ? (
                      <span className="fh-link-item__tag">already linked</span>
                    ) : (
                      <span className="fh-link-item__tag">select →</span>
                    )}
                  </button>
                );
              })}
          </div>
        </>
      ) : (
        <>
          <div className="fh-link-picked">
            <div>
              <div style={{ fontWeight: 600 }}>{picked.name}</div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                {picked.sku}
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setPicked(null)}>
              Change
            </Button>
          </div>

          <div className="row g-3 mt-2">
            <div className="col-md-6">
              <Input
                label="Supplier SKU"
                value={form.supplierSku}
                onChange={(e) =>
                  setForm({ ...form, supplierSku: e.target.value })
                }
              />
            </div>
            <div className="col-md-6">
              <Input
                label="Supplier Cost"
                type="number"
                value={form.supplierCost}
                onChange={(e) =>
                  setForm({ ...form, supplierCost: e.target.value })
                }
              />
            </div>
            <div className="col-md-6">
              <Input
                label="Lead Time (days)"
                type="number"
                value={form.leadTimeDays}
                onChange={(e) =>
                  setForm({ ...form, leadTimeDays: e.target.value })
                }
              />
            </div>
            <div className="col-md-6">
              <Input
                label="Min Order Qty"
                type="number"
                value={form.minOrderQty}
                onChange={(e) =>
                  setForm({ ...form, minOrderQty: e.target.value })
                }
              />
            </div>
            <div className="col-12">
              <Input
                label="Notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
            <div className="col-12">
              <label className="fh-checkbox">
                <input
                  type="checkbox"
                  checked={form.isPreferred}
                  onChange={(e) =>
                    setForm({ ...form, isPreferred: e.target.checked })
                  }
                />
                <span>Mark as preferred supplier for this product</span>
              </label>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
